import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SpinnerContainer } from '@/components/Containers/SpinnerContainer';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { iFormContactFields } from '@/components/Form/FormContact';
import { iFormPhoneValidationFields } from '@/components/Form/FormPhoneValidation';
import { iFormTokenCheckFields } from '@/components/Form/FormTokenCheck';
import { iFormUserAccountFields } from '@/components/Form/FormUserAccount';
import { ProductsUpdatePrivatePage } from '@/components/PrivatePages/ProductsUpdatePrivatePage';
import { NOTIFICATION_NEW_FREE_REMINDER_ID } from '@/config';
import { EUR } from '@/config/appConstants';
import { useUpdateBillingDetails } from '@/features/billing';
import { Country, getCountries } from '@/features/countries';
import { useCreateInvoicePending } from '@/features/invoices';
import { getMeInfo } from '@/features/me';
import {
  getMobileToken,
  MobileToken,
  useCreateMobileToken,
  useUpdateMobileToken,
} from '@/features/mobileToken';
import { getUserProducts, ResponseError, useDeleteUserPendingProduct } from '@/features/myProducts';
import { useCreateNotificationContact } from '@/features/notification';
import {
  useCreatePayment,
  useDeleteUserIncompleteProduct,
  useUpdatePayment,
} from '@/features/payment';
import { useProducts } from '@/features/products';
import { getProvinces, Province } from '@/features/provinces';
import { getSmsCredits, SmsCredits } from '@/features/smsCredits';
import { getTinDocumentTypes, TinDocumentType } from '@/features/tinDocumentTypes';
import { useGroupBillingDetails } from '@/hooks/useGroupBillingDetails';
import { useMe } from '@/hooks/useMe';
import { useAuth } from '@/lib/auth';
import { getAllAxiosData, GetterAxios } from '@/lib/axios';
import {
  buildGTMAddLogged,
  buildGTMCompletedBilling,
  buildGTMValidationAccount,
  sendEventGtm,
} from '@/lib/gtm/gtmUtils';
import { useNotificationStore } from '@/stores/notifications';
import { iAuthInfo, UserProductTypes, ProductTypes } from '@/types';
import {
  getFreemiumProduct,
  getMyRateURL,
  getProductsNonFreemium,
  getUserCurrency,
  getUserData,
  isAdminUserFromMe,
  isCoreProduct,
  isNewFreeUserFromMe,
} from '@/utils/helpers';
import { PaymentIntentWrapper } from '@/utils/paymentIntentWrapper';
import storage from '@/utils/storage';

export const ProductsUpdatePage = () => {
  const { dismissNotification } = useNotificationStore();
  const { getAuthInfo, newLogin } = useAuth();
  const { i18n } = useTranslation();
  const authInfo: iAuthInfo = getAuthInfo();
  const handleError = useErrorHandler();
  const { getMe, setMe, isMeSet } = useMe();
  const { setBillingDetails, getBillingDetails } = useGroupBillingDetails();
  const updateBillingDetailsMutation = useUpdateBillingDetails();
  const sendDataToGTM = useGTMDispatch();
  const [userProducts, setUserProducts] = useState<UserProductTypes[] | undefined>(undefined);
  const [smsCredits, setSmsCredits] = useState<SmsCredits | undefined>(undefined);
  const [mobileToken, setMobileToken] = useState<MobileToken>();
  const [countries, setCountries] = useState<Country[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [tinDocumentTypes, setTinDocumentTypes] = useState<TinDocumentType[]>([]);
  const [, setHasBeenReactivated] = useState<boolean>(false);
  const [hasBeenCanceled, setHasBeenCanceled] = useState<boolean>(false);
  const [statusDeleteUserPendingProductError, setStatusDeleteUserPendingProductError] = useState({
    status: 0,
    detail: '',
  });

  const groupId = authInfo.getGroupId ? authInfo.getGroupId() : '';
  const productsQuery = useProducts();

  const { isLoading: productsIsLoading = false, data: products } = productsQuery;

  const createNotificationContactMutation = useCreateNotificationContact();
  const createCreatePaymentMutation = useCreatePayment();
  const createUpdatePaymentMutation = useUpdatePayment();
  const deleteUserPendingProductMutation = useDeleteUserPendingProduct();
  const deleteUserIncompleteProductMutation = useDeleteUserIncompleteProduct();
  const createMobileTokenMutation = useCreateMobileToken();
  const updateUpdateMobileTokenMutation = useUpdateMobileToken();

  const createInvoicePendingMutation = useCreateInvoicePending();

  const billingDetails = getBillingDetails();

  useEffect(() => {
    if (!productsIsLoading && products) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
        { callback: getSmsCredits, parameters: { groupId: groupId } },
        { callback: getCountries },
        { callback: getMobileToken, parameters: { groupId: groupId } },
      ];
      const setters: any = [setUserProducts, setSmsCredits, setCountries, setMobileToken];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [productsIsLoading, products, groupId, handleError]);

  useEffect(() => {
    if (billingDetails) {
      const getters: GetterAxios[] = [
        { callback: getProvinces, parameters: { countryId: billingDetails.countryId } },
        { callback: getTinDocumentTypes, parameters: { countryId: billingDetails.countryId } },
      ];
      const setters: any = [setProvinces, setTinDocumentTypes];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [billingDetails, handleError]);

  useEffect(() => {
    if (hasBeenCanceled) {
      const getters: GetterAxios[] = [
        { callback: getUserProducts, parameters: { groupId: groupId } },
      ];
      const setters: any = [setUserProducts];

      const pageData = getAllAxiosData(getters, setters);
      pageData.catch((error) => {
        handleError(error);
      });
    }
  }, [groupId, handleError, hasBeenCanceled]);

  if (productsIsLoading || !billingDetails || !smsCredits || !mobileToken || !isMeSet()) {
    return <SpinnerContainer />;
  }

  if (!products?.length) {
    return (
      <div
        role="list"
        aria-label="countries"
        className="bg-white text-gray-500 h-40 flex justify-center items-center flex-col"
      >
        <h4 className="md__payments__title--h4">No Products Found</h4>
      </div>
    );
  }

  const onRequestInfoSubmit = async (values: iFormContactFields) => {
    sendEventGtm(buildGTMAddLogged(userData), sendDataToGTM);
    await createNotificationContactMutation.mutateAsync({
      data: values,
    });
  };

  const countryOnChange = (formValues: any, countryId: string) => {
    getProvinces({ countryId }).then((responseProvinces) => {
      const newBillingDetails = { ...billingDetails, ...formValues.values };
      newBillingDetails.countryId = countryId;
      if (
        !newBillingDetails?.companyCif ||
        !newBillingDetails?.companyCifDocumentType ||
        !newBillingDetails?.alreadySaved
      ) {
        newBillingDetails.alreadySaved = false;
      }
      getTinDocumentTypes({ countryId }).then((responseTinDocumentTypes) => {
        setBillingDetails(newBillingDetails);
        setProvinces(responseProvinces);
        setTinDocumentTypes(responseTinDocumentTypes);
      });
    });
  };

  const onCancelButton = async (product: ProductTypes | null | undefined) => {
    if (!product) {
      return;
    }

    storage.clearUserData();

    if (isCoreProduct(product)) {
      await deleteUserPendingProductMutation.mutateAsync({
        mdirectorId: product.mdirectorId,
        onErrorCallback: onDeleteUserPendingProductErrorCallback,
      });
    }

    setHasBeenCanceled(true);
  };

  const onDeleteUserPendingProductErrorCallback = ({ status, detail }: ResponseError) => {
    setStatusDeleteUserPendingProductError({
      status,
      detail,
    });
  };

  const onCancelIncompleteAction = async () => {
    storage.clearUserData();
    const userProducts = await deleteUserIncompleteProductMutation.mutateAsync({
      groupId: groupId,
    });
    setUserProducts(userProducts);
  };

  const onSubmitFormUserAccount = async (values: iFormUserAccountFields): Promise<boolean> => {
    const updateBillingDetails = await updateBillingDetailsMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });
    if (updateBillingDetails.hasError) {
      return false;
    }

    if (isNewFreeUserFromMe(getMe())) {
      !values.createdAt && sendEventGtm(buildGTMCompletedBilling(getMe()), sendDataToGTM);
      dismissNotification(NOTIFICATION_NEW_FREE_REMINDER_ID);
    }
    await newLogin(async () => true);
    await loadMe();
    if (isAdminUserFromMe(getMe()) && !values.createdAt) {
      sendEventGtm(buildGTMValidationAccount(getMe()), sendDataToGTM);
    }
    return true;
  };

  const loadMe = async () => {
    const me = await getMeInfo();
    setMe(me);
    return true;
  };

  const onSubmitFormPhoneValidation = async (values: iFormPhoneValidationFields) => {
    const mobileToken = await createMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });
    setMobileToken(mobileToken);
  };

  const onSubmitFormTokenCheck = async (values: iFormTokenCheckFields) => {
    const mobileToken = await updateUpdateMobileTokenMutation.mutateAsync({
      data: values,
      groupId: groupId,
    });

    setMobileToken(mobileToken);
    setBillingDetails({ ...billingDetails, phone: values.phone });
  };

  const userCurrency = getUserCurrency(billingDetails, storage);
  const userData = getUserData(getMe(), i18n);

  const paymentIntent = PaymentIntentWrapper({
    createCreatePaymentMutation,
    createUpdatePaymentMutation,
    setHasBeenReactivated,
    setHasBeenCanceled,
  });

  const onConfirmUnpaidMeteredErrorAction = async () => {
    await createInvoicePendingMutation.mutateAsync();
  };

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusDeleteUserPendingProductError.status}
      detail={statusDeleteUserPendingProductError.detail}
      onRequestClose={() => {
        setStatusDeleteUserPendingProductError({ status: 0, detail: '' });
      }}
    />
  );

  return statusDeleteUserPendingProductError.status > 0 ? (
    renderStatusPaymentErrorModal()
  ) : (
    <ProductsUpdatePrivatePage
      billingDetails={billingDetails}
      onRequestInfoSubmit={onRequestInfoSubmit}
      userData={userData}
      userCurrency={userCurrency || EUR}
      products={getProductsNonFreemium(products)}
      freemiumProduct={getFreemiumProduct(products)}
      userProducts={userProducts}
      countries={countries}
      provinces={provinces}
      tinDocumentTypes={tinDocumentTypes}
      countryOnChange={countryOnChange}
      getCreatePaymentIntent={paymentIntent.getCreatePaymentIntent}
      getUpdatePaymentIntent={paymentIntent.getUpdatePaymentIntent}
      onCancelButton={onCancelButton}
      onCancelIncompleteAction={onCancelIncompleteAction}
      onSubmitFormUserAccount={onSubmitFormUserAccount}
      onFinishURLRedirect={getMyRateURL()}
      smsCredits={smsCredits}
      onSubmitFormPhoneValidation={onSubmitFormPhoneValidation}
      onSubmitFormTokenCheck={onSubmitFormTokenCheck}
      mobileToken={mobileToken}
      onConfirmUnpaidMeteredErrorAction={onConfirmUnpaidMeteredErrorAction}
    />
  );
};
